import React, { useEffect, useMemo, useState } from 'react';
import { GenericForm, type GenericFormField } from '@/styles/GenericForm';
import { fetchEntityList } from '@/lib/entity-api';
import { operationalCodes } from '@/lib/cost-codes';
import { fetchCostCodes, saveItemWithOffers, type CatalogItem, type CatalogOffer, type CostCode } from '../services/catalog-api';
import { offerRowsProblem, offersPayload, rowOfOffer, type OfferRow } from '../lib/offers';
import { ItemOffersSection } from './item-offers-section';

interface ItemFormProps {
  /** null = a new item */
  item: CatalogItem | null;
  isOpen: boolean;
  onClose: () => void;
  /** the item and its vendors were saved (the list reloads) */
  onSaved: () => void;
}

/**
 * Create / edit a catalog item and its vendors in ONE popup and ONE save: name, cost code, SKU / internal code, unit, then any number of
 * vendors with their prices. The item and every vendor are persisted together (one request, one database transaction), for a new item and
 * for an existing one -- if anything fails, nothing is saved and the popup stays open with the error.
 */
export function ItemForm({ item, isOpen, onClose, onSaved }: ItemFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [codes, setCodes] = useState<CostCode[]>([]);
  const [rows, setRows] = useState<OfferRow[]>([]);
  const [original, setOriginal] = useState<CatalogOffer[]>([]);
  // saved offers taken off the item: deleted on the server with the single Save (a never-saved row just leaves `rows`)
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  // on mount too, so the list of child cost codes is ready before the popup opens
  useEffect(() => {
    fetchCostCodes().then(setCodes).catch((failure) => console.error('Error loading cost codes:', failure));
  }, [isOpen]);

  // the item's current vendors (an existing item) or none (a new one)
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setRemovedIds([]);
    if (!item) { setRows([]); setOriginal([]); return; }
    let cancelled = false;
    setLoadingOffers(true);
    fetchEntityList<any>('catalog_item_offer', { filters: { item_id: item.id } })
      .then((list) => {
        if (cancelled) return;
        const offers: CatalogOffer[] = list
          .map((row) => ({ ...row, unit_price: Number(row.unit_price), conversion_factor: row.conversion_factor === null ? null : Number(row.conversion_factor) }))
          .sort((a, b) => a.unit_price - b.unit_price);
        setOriginal(offers);
        setRows(offers.map(rowOfOffer));
      })
      .catch((failure) => { if (!cancelled) setError(failure instanceof Error ? failure.message : 'Could not load the vendors'); })
      .finally(() => { if (!cancelled) setLoadingOffers(false); });
    return () => { cancelled = true; };
  }, [isOpen, item]);

  const fields = useMemo<GenericFormField[]>(() => {
    // CHILD cost codes only (a parent only groups others); active ones, plus the one the item already has even if it was deactivated since
    const options = operationalCodes(codes, item?.cost_code_id ? [item.cost_code_id] : []).map((c) => ({ label: c.name ? `${c.code} · ${c.name}` : c.code, value: c.id }));
    return [
      { name: 'name', label: 'Name', type: 'text', required: true, row: 1 },
      {
        name: 'cost_code_id',
        label: 'Cost Code',
        type: 'select',
        required: true,
        row: 1,
        options,
        helperText: codes.length > 0 && options.length === 0 ? 'Create a Cost Code and assign it a Parent Cost Code (Catalog > Cost Codes) before creating catalog items.' : undefined,
      },
      { name: 'sku', label: 'SKU / Internal Code', type: 'text', row: 2 },
      { name: 'unit', label: 'Unit', type: 'text', row: 2 },
    ];
  }, [codes, item?.cost_code_id]);

  const initialValues = useMemo(() => ({
    name: item?.name ?? '',
    cost_code_id: item?.cost_code_id ?? '',
    sku: item?.sku ?? '',
    unit: item?.unit ?? '',
  }), [item]);

  const handleSubmit = async (data: Record<string, any>) => {
    if (saving || loadingOffers) return;
    const problem = offerRowsProblem(rows);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try {
      await saveItemWithOffers(item?.id ?? null, {
        item: { name: data.name, sku: data.sku, unit: data.unit, cost_code_id: data.cost_code_id },
        offers: offersPayload(rows, original, removedIds),
      });
      onSaved();
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Failed to save the item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <GenericForm
      title="Catalog Item"
      mode={item ? 'edit' : 'create'}
      fields={fields}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      onCancel={() => { setError(null); onClose(); }}
      isOpen={isOpen}
      error={error}
      saving={saving || loadingOffers}
      additionalSections={(
        <ItemOffersSection
          rows={rows}
          onChange={setRows}
          onRemove={(row) => {
            setRows((current) => current.filter((r) => r.key !== row.key));
            if (row.id) setRemovedIds((current) => [...current, row.id as string]);
          }}
        />
      )}
    />
  );
}
